import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatErr } from "../lib/api";
import { ArrowLeft, Mail, Loader2, CheckCircle2, KeyRound } from "lucide-react";

export default function Forgot() {
  const [identifier, setIdentifier] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!identifier.trim()) { setErr("Enter your registered email or username"); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/auth/forgot-password", { identifier: identifier.trim() });
      setSent(true);
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0B0F19] text-white px-6 py-10 flex items-center" data-testid="forgot-page">
      <div className="max-w-sm w-full mx-auto">
        <Link to="/login" className="inline-flex items-center gap-1 text-xs text-white/60 mb-6"><ArrowLeft className="w-3.5 h-3.5"/>Back to login</Link>

        <div className="w-14 h-14 rounded-2xl bg-[#FF7A00]/15 border border-[#FF7A00]/30 flex items-center justify-center mb-4">
          <KeyRound className="w-6 h-6 text-[#FF7A00]"/>
        </div>

        {!sent ? (
          <>
            <h1 className="font-outfit font-extrabold text-3xl mb-1">Forgot your password?</h1>
            <p className="text-sm text-white/60 mb-6">No worries — enter your registered email or username and we&apos;ll send you a secure reset link.</p>

            <form onSubmit={submit} className="space-y-3" data-testid="forgot-form">
              <input
                data-testid="forgot-identifier"
                value={identifier}
                onChange={e => setIdentifier(e.target.value)}
                placeholder="Registered email or username"
                autoComplete="username"
                required
                className="w-full px-4 py-3.5 rounded-2xl bg-white/5 border border-white/10 outline-none focus:border-[#FF7A00] text-white placeholder-white/40"
              />
              {err && <div className="text-[#FF2A5F] text-sm" data-testid="forgot-err">{err}</div>}
              <button disabled={busy} type="submit" data-testid="forgot-submit"
                className="w-full py-3.5 rounded-full bg-gradient-to-r from-[#FF7A00] to-[#FF7A00] text-black font-outfit font-extrabold disabled:opacity-50 active:scale-95 flex items-center justify-center gap-2">
                {busy ? <><Loader2 className="w-4 h-4 animate-spin"/>Sending…</> : <><Mail className="w-4 h-4"/>Send reset link</>}
              </button>
            </form>

            <p className="text-[11px] text-white/40 mt-4 text-center">
              Remembered it? <Link to="/login" className="text-[#FF7A00] font-bold">Login</Link>
            </p>
          </>
        ) : (
          <div data-testid="forgot-sent" className="text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mx-auto mb-3">
              <CheckCircle2 className="w-7 h-7 text-emerald-400"/>
            </div>
            <h2 className="font-outfit font-extrabold text-2xl mb-1">Check your email</h2>
            <p className="text-sm text-white/65 mb-5">
              If an account exists for <b className="text-white/90">{identifier}</b>, a password reset link has been sent.
              It expires in 1 hour.
            </p>
            <p className="text-[11px] text-white/40 mb-5">
              Didn&apos;t get it? Check spam, or try again with a different email/username.
            </p>
            <Link to="/login" data-testid="forgot-back-to-login"
              className="inline-block px-6 py-2.5 rounded-full bg-white/10 border border-white/15 font-bold text-sm">
              Back to login
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
